import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Play, Lock, Unlock, Settings, RefreshCw } from "lucide-react";
import { api, fmtINR, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function PayrollRun() {
  const { user } = useAuth();
  const canUnlock = ["admin", "hr"].includes(user?.role);
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [setOpen, setSetOpen] = useState(false);
  const [cfg, setCfg] = useState({});
  const [warnOpen, setWarnOpen] = useState(false);

  const load = (m = month) => api.get(`/payroll/preview?month=${m}`)
    .then((r) => { setD(r.data); setCfg(r.data.settings); })
    .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const run = async (path, body, msg) => {
    setBusy(true);
    try { const r = await api.post(path, body); toast.success(r.data.message || msg); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const saveCfg = async () => {
    try {
      await api.put("/hr-settings", {
        working_days: Number(cfg.working_days), half_day_factor: Number(cfg.half_day_factor),
        paid_leave_is_paid: !!cfg.paid_leave_is_paid, lwp_is_lop: !!cfg.lwp_is_lop,
        unmarked_is_absent: !!cfg.unmarked_is_absent,
      });
      setSetOpen(false); toast.success("Payroll settings saved"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const statusTone = { Draft: "bg-slate-100 text-slate-700 border-slate-200", Processing: "bg-amber-50 text-amber-700 border-amber-200", Finalized: "bg-emerald-50 text-emerald-700 border-emerald-200", Locked: "bg-red-50 text-red-700 border-red-200" };

  const risky = (d?.rows || []).filter((r) => (r.unmarked_days || 0) > 0);
  const generate = () => { setWarnOpen(false); run("/payroll/generate", { month }, "Payslips generated"); };
  const onGenerateClick = () => (risky.length ? setWarnOpen(true) : generate());

  return (
    <div data-testid="payroll-run">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Banknote className="w-6 h-6 text-[#0B132B]" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Payroll Run</h1>
            <p className="text-sm text-slate-500 mt-0.5">Attendance-based salary preview for {d?.month_label || month}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge data-testid="payroll-status-badge" variant="outline" className={`rounded-sm ${statusTone[d?.status] || statusTone.Draft}`}>{d?.status || "Draft"}</Badge>
          <Input data-testid="payroll-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-white" />
          <Button data-testid="payroll-refresh" size="icon" variant="outline" className="rounded-sm h-9 w-9" onClick={() => load()}><RefreshCw className="w-4 h-4" /></Button>
          <Button data-testid="payroll-settings-btn" variant="outline" className="rounded-sm gap-1.5" onClick={() => setSetOpen(true)}><Settings className="w-4 h-4" /> <span className="hidden sm:inline">Settings</span></Button>
          {!d?.locked && (
            <Button data-testid="payroll-generate-btn" disabled={busy} onClick={onGenerateClick} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Play className="w-4 h-4" /> Generate Slips</Button>
          )}
          {!d?.locked ? (
            <Button data-testid="payroll-finalize-btn" disabled={busy} onClick={() => window.confirm(`Finalize ${d?.month_label}? Attendance for this month will be locked.`) && run("/payroll/finalize", { month }, "Payroll finalized")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1.5"><Lock className="w-4 h-4" /> Finalize</Button>
          ) : canUnlock && (
            <Button data-testid="payroll-unlock-btn" disabled={busy} onClick={() => run("/payroll/unlock", { month }, "Payroll unlocked")} variant="outline" className="rounded-sm gap-1.5 border-red-200 text-red-700"><Unlock className="w-4 h-4" /> Unlock</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <div className="bg-[#0B132B] text-white rounded-lg p-5 shadow-sm" data-testid="payroll-total-net">
          <div className="text-xs font-semibold uppercase tracking-wider text-white/60">Net Payable ({d?.month_label})</div>
          <div className="text-2xl font-mono font-semibold tabular-nums mt-2">{fmtINR(d?.total_net || 0)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Gross Earnings</div>
          <div className="text-2xl font-mono font-semibold tabular-nums text-slate-900 mt-2">{fmtINR(d?.total_gross || 0)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Employees / Working Days</div>
          <div className="text-2xl font-mono font-semibold tabular-nums text-slate-900 mt-2">{d?.rows?.length || 0} <span className="text-base text-slate-400">/ {d?.settings?.working_days ?? "-"}</span></div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm" data-testid="payroll-preview-table">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
            {["Employee", "Eligible", "Present", "Half", "Absent", "Leave", "LWP", "Unmarked", "Payable", "Gross", "Deductions", "Net Salary", "Slip"].map((h, i) => (
              <th key={h} className={`px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${i >= 1 ? "text-right" : ""}`}>{h}</th>))}
          </tr></thead>
          <tbody>
            {!d ? <tr><td colSpan={13} className="px-4 py-16 text-center text-slate-400">Loading preview...</td></tr>
              : d.rows.length === 0 ? <tr><td colSpan={13} className="px-4 py-16 text-center text-slate-400" data-testid="payroll-empty">No active employees</td></tr>
              : d.rows.map((r) => (
                <tr key={r.employee_id} data-testid={`payroll-row-${r.employee_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium text-slate-800">{r.employee_name}<div className="text-xs text-slate-400">{r.department || "-"}{r.join_date ? ` · joined ${r.join_date}` : ""}</div></td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{r.eligible_days}{r.joined_mid_month && <span className="ml-1 text-[10px] text-amber-600">pro-rata</span>}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{r.present_days}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{r.half_days}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-red-700">{r.absent_days}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-blue-700">{r.leave_days}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-red-700">{r.lwp_days}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{r.unmarked_days}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{r.payable_days}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtINR(r.gross_pay)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-red-600">{fmtINR(r.total_deductions)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold">{fmtINR(r.net_pay)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Badge variant="outline" className={`rounded-sm text-[10px] ${r.slip_exists ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.slip_exists ? "Generated" : "Pending"}</Badge>
                  </td>
                </tr>))}
          </tbody>
        </table>
      </div>
      {d?.rows?.some((r) => r.marked_days === 0) && (
        <p className="text-xs text-amber-700 mt-3" data-testid="payroll-warning">Some employees have no attendance marked for this month — mark daily attendance first for an accurate salary.</p>
      )}

      <Dialog open={warnOpen} onOpenChange={setWarnOpen}>
        <DialogContent className="max-w-lg" data-testid="payroll-sanity-dialog">
          <DialogHeader><DialogTitle className="font-head text-amber-700">Attendance is incomplete</DialogTitle>
            <DialogDescription>
              {risky.length} employee(s) have unmarked days in {d?.month_label}. Unmarked days are treated as unpaid,
              so their salary will be lower. Mark daily attendance first for an accurate payroll.
            </DialogDescription></DialogHeader>
          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
            {risky.map((r) => (
              <div key={r.employee_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`payroll-warn-${r.employee_id}`}>
                <span className="font-medium text-slate-800">{r.employee_name}</span>
                <span className="text-slate-500 tabular-nums">{r.unmarked_days} unmarked · {r.payable_days} payable</span>
              </div>))}
          </div>
          <DialogFooter>
            <Button data-testid="payroll-warn-cancel" variant="outline" className="rounded-sm" onClick={() => setWarnOpen(false)}>Cancel & mark attendance</Button>
            <Button data-testid="payroll-warn-continue" disabled={busy} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={generate}>Generate anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Payroll Settings</DialogTitle>
            <DialogDescription>Calculation rules used for every salary slip. Your salary structure stays unchanged.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Working Days per Month</label>
              <Input data-testid="cfg-working-days" type="number" min="1" value={cfg.working_days ?? ""} onChange={(e) => setCfg({ ...cfg, working_days: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Half Day counts as (days of LOP)</label>
              <Input data-testid="cfg-half-day" type="number" step="0.1" min="0" max="1" value={cfg.half_day_factor ?? ""} onChange={(e) => setCfg({ ...cfg, half_day_factor: e.target.value })} className="bg-white" /></div>
            {[["paid_leave_is_paid", "Approved leave is paid"], ["lwp_is_lop", "LWP causes loss of pay"], ["unmarked_is_absent", "Treat unmarked days as absent"]].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-sm text-slate-700">{label}</span>
                <Switch data-testid={`cfg-${k}`} checked={!!cfg[k]} onCheckedChange={(v) => setCfg({ ...cfg, [k]: v })} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="cfg-save" onClick={saveCfg} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
